import { Pin, PinOff } from '@tunasong/icons'
import type { FC } from 'react'
import { useNetworkStatus } from '../network/use-network-status.js'
import { ListItemIcon, ListItemText, MenuItem, type MenuItemProps } from '../primitives/index.js'
import { bandLabel } from './band-pin-copy.js'
import { requestBandPin } from './band-pin-store.js'
import type { FavoriteControlProps } from './favorite-props.js'
import { useBandPin } from './use-band-pin.js'

type BandPinMenuItemProps = FavoriteControlProps<MenuItemProps>

/** Long band names are cut in the menu; the full name is the item's title (spec, Microcopy) */
const menuNameLimit = 24
const menuName = (name: string) =>
  name.length > menuNameLimit ? `${name.slice(0, menuNameLimit - 1).trimEnd()}…` : name

/**
 * "Pin to {Band name}" beside "Pin to dashboard" (GH #2822). Offered only for an item inside a band space, to
 * members who can edit the band. Like the personal pin it is never disabled for being full: the host says so.
 */
export const BandPinMenuItem: FC<BandPinMenuItemProps> = props => {
  const { onClick, entity, ...restProps } = props
  const { band, canEdit, pinned } = useBandPin(entity)
  const { status } = useNetworkStatus()

  if (!(band && canEdit)) {
    return null
  }
  const name = bandLabel(band)
  const shown = menuName(name)
  const label = pinned ? `Unpin from ${shown}` : `Pin to ${shown}`
  const Icon = pinned ? PinOff : Pin

  return (
    <MenuItem
      /** No optimistic pin that later disappears: offline, the item is unavailable as elsewhere in the app */
      disabled={status === 'offline'}
      title={shown === name ? undefined : pinned ? `Unpin from ${name}` : `Pin to ${name}`}
      onClick={ev => {
        ev.preventDefault()
        ev.stopPropagation()
        requestBandPin({ bandId: band.id, entityId: entity.id, action: pinned ? 'unpin' : 'pin' })
        if (onClick) {
          onClick(ev)
        }
      }}
      {...restProps}
    >
      <ListItemIcon>
        <Icon color={pinned ? 'var(--tuna-palette-primary-main)' : 'currentColor'} />
      </ListItemIcon>
      <ListItemText primary={label} />
    </MenuItem>
  )
}

export default BandPinMenuItem
